import React, { type PropsWithChildren, useRef } from 'react';
import { BlurView } from 'expo-blur';
import { StyleSheet, type StyleProp, type View, type ViewStyle } from 'react-native';
import Animated, {
  interpolateColor,
  type WithTimingConfig,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { useHovered } from '../../../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarGlassView = (props: Props) => {
  const { children, canHover = false, rounded = 'full', style } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const containerRef = useRef<View>(null);
  const isHovered = useHovered(containerRef);

  // ─── Animated Values ───────────────────────────────────────────────────────

  const hoverSV = useDerivedValue(() => {
    if (canHover && isHovered) {
      return withTiming(1, GLASS_TIMING_CONFIG);
    }

    return withTiming(0, GLASS_TIMING_CONFIG);
  });

  // ─── Animated Styles ───────────────────────────────────────────────────────

  const backgroundContainerAnimatedStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        hoverSV.value,
        GLASS_HOVER_INPUT_RANGE,
        GLASS_BACKGROUND_COLORS,
      ),
    };
  });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const roundedStyle = roundedStyles[rounded];
  const containerStyles = [styles.container, roundedStyle, style];
  const blurContainerStyles = [styles.blurContainer, roundedStyle];
  const backgroundContainerStyles = [
    styles.backgroundContainer,
    roundedStyle,
    backgroundContainerAnimatedStyle,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Animated.View ref={containerRef} style={containerStyles}>
      <BlurView
        intensity={GLASS_INTENSITY}
        pointerEvents="none"
        style={blurContainerStyles}
        tint="light"
      />

      <Animated.View pointerEvents="none" style={backgroundContainerStyles} />

      {children}
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GLASS_INTENSITY = 80;
const GLASS_HOVER_INPUT_RANGE = [0, 1];
const GLASS_BACKGROUND_COLORS = ['rgba(255, 255, 255, 0.7)', 'rgba(255, 255, 255, 0.9)'];
const GLASS_TIMING_CONFIG: WithTimingConfig = { duration: theme.transitions.short };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    boxShadow: '0px 8px 40px 4px rgba(14, 21, 14, 0.09)',
    overflow: 'hidden',
  },
  blurContainer: {
    ...StyleSheet.absoluteFill,
  },
  backgroundContainer: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
});

const roundedStyles = StyleSheet.create<Record<Rounded, ViewStyle>>({
  large: { borderRadius: theme.radius.xlarge },
  full: { borderRadius: 1000 },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  canHover?: boolean;
  rounded?: Rounded;
  style?: StyleProp<ViewStyle>;
}>;

type Rounded = 'full' | 'large';
